import * as React from "react";
import NoDataPlaceholder from "src/components/no-data-placeholder";
import { Box, Chip, Link, Stack } from "@mui/material";
import styled, { DefaultTheme } from "styled-components";
import { formatDistanceToNow, isValid } from "date-fns";
import TimeSplitter from "src/utils/time-splitter";

const Title = styled.h1`
    font-size: 12px;
    font-weight: 400;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const TitleLink = styled(Link)`
    font-size: 12px;
    font-weight: 400;
    margin-bottom: 0px;
    color: #ffffff;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;
`;

const BodyText = styled.p`
    font-size: 10px;
    margin: 0;
    color: ${({ theme, type }: { theme: DefaultTheme; type: string }) => {
        if (type === "Bug") return theme.palette.background.bugs;
        if (type === "Feature") return theme.palette.background.features;
        if (type === "Task") return theme.palette.background.tasks;
        return theme.palette.secondary.main;
    }};
`;

const Container = styled(Stack)`
    border: 1px solid #585858;
    border-radius: 6px;
    padding: 8px;
    white-space: nowrap;
`;

const TemplateLinkList = ({ data, parameters }) => {
    if (!data || data.length === 0) return <NoDataPlaceholder variant="list" errorMessage={null} />;
    const style: { [key: string]: string } = {};
    if (parameters.direction === "horizontal") {
        style.width = "98%";
        style.overflowX = "auto";
    } else {
        style.width = "100%%";
        style.height = "100%";
    }
    return (
        <Box
            style={{
                overflow: "auto",
                width: "100%",
            }}
        >
            <Stack
                direction={parameters.direction === "horizontal" ? "row" : "column"}
                spacing={1}
                pr={2}
                pb={parameters.direction === "horizontal" ? 1 : 0}
                mb={parameters.direction === "horizontal" ? 1 : 0}
                style={style}
            >
                {data.map((item) => {
                    return (
                        <Container direction="column">
                            {parameters.headers.map((header) => {
                                if (header.type === "link") {
                                    return (
                                        <TitleLink
                                            underline="hover"
                                            href={item[header.linkProperty]}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {item[header.property]}
                                        </TitleLink>
                                    );
                                }
                                return <Title>{item[header.property]}</Title>;
                            })}
                            <Stack direction="row" spacing={1} alignItems="center">
                                {parameters.columns.map((column) => {
                                    if (column.type === "string") {
                                        return (
                                            <BodyText type={item[column.property]}>
                                                {item[column.property]}
                                            </BodyText>
                                        );
                                    }
                                    if (column.type === "number") {
                                        const val = item[column.property];
                                        return (
                                            <BodyText type="">
                                                {column.name ? `${column.name}: ` : ""}
                                                {val != null ? Number(val).toLocaleString() : "—"}
                                            </BodyText>
                                        );
                                    }
                                    if (column.type === "chip") {
                                        const val = item[column.property];
                                        if (!val) return null;
                                        const chipColorMap: Record<
                                            string,
                                            "success" | "default" | "primary"
                                        > = {
                                            active: "success",
                                            closed: "default",
                                        };
                                        const color: "success" | "default" | "primary" =
                                            chipColorMap[val] ?? "primary";
                                        return (
                                            <Chip
                                                label={val}
                                                size="small"
                                                color={color}
                                                variant="outlined"
                                                sx={{ fontSize: "10px", height: "18px" }}
                                            />
                                        );
                                    }
                                    if (column.type === "ago") {
                                        const date = new Date(item[column.property]);
                                        if (isValid(date)) {
                                            return (
                                                <BodyText style={{ opacity: "0.5" }}>
                                                    {formatDistanceToNow(date)} ago
                                                    {TimeSplitter(
                                                        item[column.property],
                                                        "until-now"
                                                    )}
                                                </BodyText>
                                            );
                                        }
                                    }
                                    return null;
                                })}
                            </Stack>
                        </Container>
                    );
                })}
            </Stack>
        </Box>
    );
};

export default TemplateLinkList;
